<template>
  <div
    class="ai-dialog-footer flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
  >
    <!-- Unchecked info -->
    <div
      v-if="state === 'unchecked' || state === 'idle'"
      class="ai-dialog-footer__info flex items-center gap-2"
    >
      <svg-icon name="huge-ai" class="w-3.5 h-3.5 text-indigo-500" />
      {{ $t("Готово к запуску. Это займёт несколько секунд.") }}
    </div>

    <!-- Triggering / Running info -->
    <div
      v-else-if="state === 'triggering' || state === 'running'"
      class="ai-dialog-footer__info ai-dialog-footer__info--running flex items-start gap-2"
    >
      <div
        class="w-1.5 h-1.5 rounded-full bg-indigo-500 ai-pulse shrink-0 mt-1.5"
      ></div>
      <div class="flex flex-col min-w-0">
        <div class="flex items-center gap-1">
          {{
            state === "triggering"
              ? $t("Запускаем AI-проверку…")
              : $t("AI анализирует документы")
          }}
          <template v-if="state === 'running'">
            ·
            <div class="font-semibold text-indigo-600">{{ progress }}%</div>
          </template>
        </div>
        <div class="ai-dialog-footer__hint">
          {{
            $t(
              "Обычно занимает около минуты. Уведомление придёт когда проверка завершится — можно закрыть окно и продолжить работу."
            )
          }}
        </div>
      </div>
    </div>

    <!-- Error info -->
    <div
      v-else-if="state === 'error'"
      class="ai-dialog-footer__info flex items-center gap-2"
    >
      <div
        class="ai-dialog-footer__danger-tile flex items-center justify-center shrink-0"
      >
        <svg-icon name="sli-danger" class="w-3.5 h-3.5 shrink-0 fill-none" />
      </div>
      {{ $t("Сервис AI временно недоступен") }}
    </div>

    <!-- Stale info -->
    <div
      v-else-if="state === 'stale'"
      class="ai-dialog-footer__info flex items-center gap-2"
    >
      <svg-icon name="huge-warning" class="w-3.5 h-3.5 text-orange-500" />
      {{ $t("Загружены новые документы — рекомендуем перепроверку") }}
    </div>

    <!-- Done info -->
    <div v-else class="ai-dialog-footer__info flex items-center gap-2">
      <div
        v-if="refusalProb > 0"
        class="ai-dialog-footer__danger-tile flex items-center justify-center shrink-0"
      >
        <svg-icon name="sli-danger" class="w-3.5 h-3.5 shrink-0 fill-none" />
      </div>
      <div v-if="refusalProb > 0" class="flex items-center gap-1">
        {{ $t("Риск отказа —") }}
        <div class="font-semibold text-red-500">{{ refusalProb }}%</div>
      </div>
      <div v-else class="flex items-center gap-2">
        <svg-icon name="huge-check" class="w-3.5 h-3.5 text-green-500" />
        {{ $t("AI не нашёл проблем в документах.") }}
      </div>
    </div>

    <!-- Buttons -->
    <div class="flex items-center gap-2 flex-wrap justify-end">
      <template v-if="state === 'unchecked' || state === 'idle'">
        <Button
          variant="ghost"
          class="flex-1 md:flex-initial"
          @click="$emit('cancel')"
          >{{ $t("Отмена") }}</Button
        >
        <Button
          class="gap-2 flex-1 md:flex-initial !bg-indigo-500 hover:!bg-indigo-600 !text-white border-0"
          @click="$emit('start')"
        >
          <svg-icon name="huge-ai" class="w-4 h-4" />
          {{ $t("Запустить проверку") }}
        </Button>
      </template>
      <template v-else-if="state === 'triggering' || state === 'running'">
        <Button
          variant="ghost"
          class="flex-1 md:flex-initial"
          @click="$emit('cancel')"
          >{{ $t("Закрыть") }}</Button
        >
        <Button disabled class="gap-2 flex-1 md:flex-initial border-0">
          <svg-icon name="huge-ai" class="w-4 h-4 ai-spin" />
          {{ $t("Анализ…") }}
        </Button>
      </template>
      <template v-else-if="state === 'error'">
        <Button
          variant="ghost"
          class="flex-1 md:flex-initial"
          @click="$emit('cancel')"
          >{{ $t("Закрыть") }}</Button
        >
        <Button
          class="gap-2 flex-1 md:flex-initial !bg-indigo-500 hover:!bg-indigo-600 !text-white border-0"
          @click="$emit('restart')"
        >
          <svg-icon name="huge-ai" class="w-4 h-4" />
          {{ $t("Повторить") }}
        </Button>
      </template>
      <template v-else>
        <Button
          variant="outline"
          class="gap-2 flex-1 md:flex-initial"
          @click="$emit('restart')"
        >
          <svg-icon name="huge-ai" class="w-4 h-4 fill-none" />
          {{ state === "stale" ? $t("Перепроверить") : $t("Перезапустить") }}
        </Button>
        <Button
          variant="outline"
          class="flex-1 md:flex-initial !text-red-600 !border-red-200 hover:!bg-red-50"
          @click="$emit('ignore')"
        >
          {{ $t("Отказать в совершении") }}
        </Button>
        <Button
          class="flex-1 md:flex-initial bg-gray-50 hover:bg-gray-100"
          @click="$emit('fix')"
          variant="outline"
        >
          <svg-icon name="huge-check" class="w-4 h-4" />
          {{ $t("Совершить надпись") }}
        </Button>
      </template>
    </div>
  </div>
</template>

<script setup>
import Button from "@/shared/ui/button/Button.vue";

defineProps({
  state: { type: String, default: "done" },
  progress: { type: Number, default: 0 },
  refusalProb: { type: Number, default: 0 },
});
defineEmits(["start", "cancel", "restart", "ignore", "fix"]);
</script>

<style lang="scss" scoped>
.ai-dialog-footer {
  @apply border-0 border-t border-solid border-border bg-muted/30;
  padding: 12px 14px;

  &__info {
    font-size: 13px;
    line-height: 1.4;
    color: rgb(var(--text-gray-500));

    &--running {
      color: rgb(var(--text-gray-700));
    }
  }

  &__danger-tile {
    width: 24px;
    height: 24px;
    @apply rounded border border-solid border-red-100 bg-red-50;
    color: rgb(var(--color-red-400));
  }

  &__hint {
    font-size: 11px;
    line-height: 1.4;
    color: rgb(var(--color-gray-500));
    margin-top: 2px;
  }
}

.ai-pulse {
  animation: ai-footer-pulse 1.4s ease-in-out infinite;
}

.ai-spin {
  animation: ai-footer-spin 1.6s linear infinite;
}

@keyframes ai-footer-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgb(99 102 241 / 0.5);
  }
  50% {
    opacity: 0.6;
    transform: scale(0.9);
    box-shadow: 0 0 0 3px rgb(99 102 241 / 0);
  }
}

@keyframes ai-footer-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
